import {Provider} from "./embed.provider";
import {EmbedManager} from "./embed.manager";
import {ScriptLoader} from "../dom/script.manager";


/**
 * Embed wrapper handling consent actions, loading embeds and placeholder management.
 */
export class ConsentEmbed extends HTMLElement {

    #shown = false;

    private get provider(): Provider {
        return this.getAttribute('provider') as Provider;
    }

    /**
     * Register that another element will want to load a script later on; synchronized after all
     * custom elements have resolve.
     */
    connectedCallback() {
        ScriptLoader.getInstance().registerScript(this.provider, this);
        setTimeout(() => this.setup(), 0);
    }

    /**
     * Initialize state. Render embed if provider has consent. Otherwise, hook into placeholder to get consent.
     * Will always subscribe to change events from manager.
     * @private
     */
    private setup() {
        if (EmbedManager.getInstance().providerHasConsent(this.provider)) {
            this.renderEmbed();
        } else {
            this.hookUserDecisionButton();
        }
        EmbedManager.getInstance().subscribeToChanges(() => this.recheckAfterEmbedUpdate());
    }

    /**
     * Identifies the user consent button and listens for user action. Will set an active state and after
     * the CSS transition finished (visual cue) the user intent is signaled to the manager.
     * @private
     */
    private hookUserDecisionButton() {
        const userActionButton = this.querySelector<HTMLDivElement>('.embed-wrap .action .switch');
        if (!userActionButton) return;
        userActionButton.addEventListener(
            'click',
            () => {
                // We only want to listen to the transition to "active" class
                // If this fails, we've established a 2s fallback
                const forwardConsent = Promise.withResolvers<void>();
                forwardConsent.promise.then(
                    () => EmbedManager.getInstance().setProviderConsent(this.provider, true),
                );
                userActionButton.addEventListener('transitionend', () => forwardConsent.resolve(), {once: true});
                setTimeout(() => forwardConsent.resolve(), 2000);  // Promise can only resolve once -> save to call twice!
                userActionButton.classList.add('active');
            },
            {once: true},
        );
    }

    /**
     * Each embed presents a one click solution to revoke consent. This hooks up the UI action.
     * @param footer
     * @private
     */
    private hookRevokeLink(footer: HTMLElement) {
        const link = footer.querySelector('a[data-role=revoke]');
        if (!link) return;
        link.addEventListener(
            'click',
            () => EmbedManager.getInstance().setProviderConsent(this.provider, false),
        );
    }

    /**
     * Loads the actual embed. The placeholder is removed, the embed is loaded from template and the footer
     * is extended with privacy policy and revoke action. Finally, loading eventual scripts is scheduled.
     * @private
     */
    private renderEmbed() {
        const contentArea = this.querySelector('section');
        const placeholder = this.querySelector('.embed-wrap');
        const footer = this.querySelector('footer');
        const embedTemplate = this.querySelector<HTMLTemplateElement>('template[data-role=embed]');
        const footerActionTemplate = this.querySelector<HTMLTemplateElement>('footer template[data-role=privacy-actions]');
        if (!contentArea || !placeholder || !footer || !embedTemplate || !footerActionTemplate) return;

        placeholder.remove();
        contentArea.appendChild(document.importNode(embedTemplate?.content, true));
        footer.appendChild(document.importNode(footerActionTemplate?.content, true));
        embedTemplate.remove();
        footerActionTemplate.remove();

        this.hookRevokeLink(footer);
        this.#shown = true;

        void scheduler.postTask(() => this.loadScript(), {priority: 'background'});
    }

    /**
     * Callback for manager updates. Checking if consent state has changed.
     * If we got new consent we render embed. If we've lost consent, we apply CSS
     * to signal the embed is going away (visual cue). Manager will reload page.
     * @private
     */
    private recheckAfterEmbedUpdate() {
        if (EmbedManager.getInstance().providerHasConsent(this.provider)) {
            if (!this.#shown) this.renderEmbed();
        } else {
            if (this.#shown) this.style.opacity = '0.1';
        }
    }

    /**
     * As multiple embeds can be present and we only ever want to load the script once, we delegate this task
     * to a script manager.
     * @private
     */
    private loadScript() {
        const template = this.querySelector<HTMLTemplateElement>('template[data-role=embed-script]');
        if (!template) return;
        ScriptLoader.getInstance().requestSynchronizedScriptLoad(this.provider, this, template);
    }
}
